/*
*   File : verify-bank.js
*   Author : https://evoqins.com
*   Description : Bank verification modal
*   Integrations : NA
*   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Components
import { FilePicker } from '../Form-Elements';
import { PrimaryButton } from '../Buttons';
import { Icon } from '../Icon';
import { _checkFileSize } from '../../Helper';
import { APIService, awsService } from '../../Service';

// Images
import Pending from "../../Assets/Images/status-pending.png";
import CloseBtn from "../../Assets/Images/close-btn-white.svg";
import BankVerifyLoader from "../../Assets/Images/KYC/bank-verify-loader.gif";

const ACCEPTED_DOCUMENTS = ["Bank statement", "Passbook (front page)", "Cancelled cheque"];
const INFO = ["Your full name (as per bank records)", "Account number", "IFSC code"];
const RETRY_LIMIT = 9; // 9 retries plus initial call

function VerifyBankModal(props) {

    const [view, setView] = useState("upload");
    const [bankProof, setBankProof] = useState(null);
    const [bankProofUrl, setBankProofUrl] = useState(null);
    const [bankProofLoading, setBankProofLoading] = useState(false);
    const [bankProofError, setBankProofError] = useState("");

    useEffect(() => {
        const allowed_views = ["upload", "verifying", "pending"];
        if (allowed_views.includes(props.selectedView)) {
            setView(props.selectedView);
        }
    }, [props.selectedView]);



    const _closeModal = () => {
        const close_btn = document.getElementById("verify-bank-modal-close")
        close_btn?.click();
        props.onSuccess?.();
    };

    const _handleContinue = () => {
        setBankProofLoading(true);
        // API call to upload bank proof
        _uploadBankProof();
    };

    const _handleSelectFile = (file) => {
        if (file && file?.target?.files?.length !== 0) {

            const is_file_size_valid = _checkFileSize(file.target.files[0].size);

            if (is_file_size_valid === false) {
                setBankProofError("Maximum file size should be 5mb only");
                return;
            } else {
                setBankProofLoading(true);
                _getPresignedUrl(file);
            }

        } else {
            setBankProof(null);
        }

    };


    // API calls ----------------------/ 

    async function _getPresignedUrl(file) {

        let file_name = file.target.files[0].name;

        let is_post = true;
        let url = 'file/upload';
        let data = {
            "type": file.target.files[0].type,
            "file": file_name,
            "is_public": true
        };

        try {
            let response = await APIService(is_post, url, data);
            let aws_URL = '';
            if (response.status_code == 200) {

                aws_URL = response.data.upload_info.url;
                let view_url = response.data.view_info;

                let file_param = new File(
                    [file.target.files[0]],
                    file.target.files[0].name,
                    { type: file.target.files[0].type }
                );

                try {
                    let awsResponse = await awsService(response.data.upload_info.fields, file_param, aws_URL);

                    if (awsResponse.status == 'ok') {

                        setBankProof(file.target.files[0]);
                        setBankProofUrl(view_url);
                        setBankProofError("");
                        setBankProofLoading(false);

                    } else {
                        toast.dismiss();
                        toast.error(awsResponse.message, { className: "e-toast" });
                        setBankProofLoading(false);
                    }
                } catch (err) {
                    toast.dismiss();
                    toast.error(err.message, { className: "e-toast" });
                    setBankProofLoading(false);
                }
            } else {
                setBankProofLoading(false);
            }

        } catch (err) {
            setBankProofLoading(false);
        }
    };

    const _verifyBank = async (retry = RETRY_LIMIT) => {

        let api_version = 3;
        let is_post = false;
        let url = 'kyc/check-bank-verification';
        let data = null;
        let token = sessionStorage.getItem('access_token');

        try {
            let response = await APIService(is_post, url, data, token, api_version);

            if (response.status_code === 200) {

                if (response.data.status === "SUCCESS") {
                    _closeModal();
                } else if (response.data.status === "FAILED") {
                    setView("upload");
                    setBankProofUrl(null);
                    setBankProof(null);
                    setBankProofError("");
                    toast.dismiss();
                    toast.error(response.data.message, { className: "e-toast" });
                } else {
                    await new Promise((resolve) => setTimeout(resolve, 5000));

                    let re_try_limit = retry ?? RETRY_LIMIT;

                    if (!re_try_limit) {
                        toast.dismiss();
                        toast.error("Bank added successfully, but verification is pending", {
                            className: "e-toast",
                        });
                    }

                    await _verifyBank(--re_try_limit);
                }
            } else {
                toast.dismiss();
                toast.error(response.message, { className: "e-toast" });
                setView("upload");
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message, { className: "e-toast" });
            setView("upload");
        }
    };

    const _uploadBankProof = () => {
        let is_post = true;
        let api_version = 3;
        let url = 'kyc/add-bank-proof';
        let data = {
            "bank_id": props.bankId || null,
            "bank_proof_url": bankProofUrl
        };

        APIService(is_post, url, data, null, api_version).then((response) => {

            if (response.status_code === 200) {
                if (props.flowType === 2) { // manage bank page
                    setView("verifying");
                    _verifyBank(RETRY_LIMIT, true);
                } else {
                    setView("pending"); // uploade success show verification pending view
                }
            } else {
                toast.dismiss();
                toast.error(response.message, { className: "e-toast" });
                setBankProofLoading(false);
            }
        }).catch((error) => {
            toast.dismiss();
            toast.error(error.message, { className: "e-toast" });
            setBankProofLoading(false);
        });

    };

    return (
        <div className="modal fade e-manage-modal"
            id="verify-bank-modal"
            tabIndex="-1"
            data-bs-backdrop="static"
            aria-labelledby="VerifyBankModalLabel"
            aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">

                <img src={CloseBtn}
                    id="verify-bank-modal-close"
                    draggable={false}
                    className='cursor-pointer d-none'
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    alt='close'
                    width={32}
                    height={32}
                />

                {
                    view === "verifying" ?
                        <div className="modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow">
                            <div className="modal-gradient-bg d-flex flex-column align-items-center justify-content-between modal-body p-4 e-border-radius-24">
                                <img src={BankVerifyLoader}
                                    className="object-fit-contain mt-4"
                                    draggable={false}
                                    alt="bank verification loader" />
                                <p className="e-font-14-res text-center e-text-charleston-green e-alt-font-poppins e-font-18 e-font-weight-600 mb-2">
                                    We are verifying your bank details
                                </p>
                                <p className="e-font-12-res text-center e-text-dark-liver e-alt-font-poppins e-font-14 e-font-weight-400 line-height-26px mb-0">
                                    Please keep the app open while this process completes.
                                </p>
                            </div>
                        </div>
                        :
                        view === "pending" ?
                            <div className=" modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow ">
                                <img src={CloseBtn}
                                    draggable={false}
                                    className='cursor-pointer float-end align-self-end position-absolute top-0 end-0 m-4 z-2'
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    alt='close'
                                    width={32}
                                    height={32}
                                    onClick={props.onSuccess}
                                />
                                <div className="modal-gradient-bg d-flex flex-column align-items-center modal-body p-4 e-border-radius-24">

                                    <img className="img-fluid col-6 mt-4 pt-3"
                                        draggable={false}
                                        src={Pending}
                                        alt="payment status image" />
                                    <p className="e-font-14-res text-center e-text-charleston-green e-alt-font-poppins e-font-18 e-font-weight-600 mt-4 mb-2">
                                        We're verifying your details
                                    </p>
                                    <p className="e-font-12-res line-height-20px-res col-sm-8 mx-auto text-center e-text-dark-liver e-alt-font-poppins e-font-14 e-font-weight-400 line-height-26px text-center mx-auto mb-4">
                                        This usually takes up to 12 hours. We'll notify you once it's complete.
                                    </p>
                                    <PrimaryButton name={props.buttonName || "Go to home"}
                                        dismiss={"modal"}
                                        className="e-border-radius-10 padding-12px-tb col-7 mx-auto"
                                        onPress={props.onSuccess} />
                                </div>
                            </div>
                            :
                            <div className="modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow">
                                <div className="modal-header justify-content-space-between pt-4 px-3 px-sm-4 pb-4 border-0">
                                    <h6 className="mb-0 e-text-charleston-green e-font-18 e-alt-font-poppins e-font-weight-600 line-height-32px">
                                        Verify your bank account
                                    </h6>
                                    <img src={require("../../Assets/Images/close-btn.svg").default}
                                        draggable={false}
                                        className="cursor-pointer"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        alt="close"
                                        width={32}
                                        height={32}
                                        onClick={props.onClose} />
                                </div>
                                <div className="modal-body pb-4 px-3 px-sm-4 pt-0">
                                    <div className="e-bg-light-error e-border-radius-16 p-3 mb-3 d-flex gap-2 align-items-center">
                                        <Icon icon="info"
                                            size={16}
                                            color="#23262F"
                                            className="mt-1" />
                                        <p className="mb-0 e-font-12 e-font-12-res e-alt-font-poppins e-font-weight-400 e-text-charleston-green line-height-20px">
                                            We couldn't verify your bank account. Please upload a valid document to complete verification.
                                        </p>
                                    </div>

                                    <div className="e-bg-cultured e-border-radius-16 p-3">
                                        <div id='verify-bank-document-image'>
                                            <FilePicker
                                                id="verify-bank-document"
                                                label="Upload bank document*"
                                                type="file"
                                                className="w-100"
                                                labelStyle="e-bg-cultured-label"
                                                accept=".png, .jpg, .jpeg"
                                                isFileUploading={bankProofLoading}
                                                file={bankProof}
                                                error={bankProofError}
                                                selectFile={_handleSelectFile}
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <p className="mb-2 e-text-charleston-green e-font-12 e-font-12-res e-alt-font-poppins e-font-weight-500 line-height-16px">
                                                Accepted documents
                                            </p>
                                            <ul className="mb-3 ps-3">
                                                {ACCEPTED_DOCUMENTS.map((doc_name, id) => (
                                                    <li key={id}
                                                        className="e-text-sonic-silver e-font-10 e-font-10-res e-alt-font-poppins e-font-weight-400 line-height-16px">
                                                        {doc_name}
                                                    </li>
                                                ))}

                                            </ul>

                                            <p className="mb-2 e-text-charleston-green e-font-12 e-font-12-res e-alt-font-poppins e-font-weight-500 line-height-16px">
                                                Make sure it includes:
                                            </p>
                                            <ul className="mb-0 ps-3">
                                                {INFO.map((info, id) => (
                                                    <li key={id}
                                                        className="e-text-sonic-silver e-font-10 e-font-10-res e-alt-font-poppins e-font-weight-400 line-height-16px">
                                                        {info}
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className="e-bg-cornsilk e-border-radius-16 p-3 d-flex gap-2 align-items-center mt-3">
                                                <Icon icon="info"
                                                    size={16}
                                                    color="#C7851E"
                                                    className="mt-1" />
                                                <p className="mb-0 e-text-ochre e-font-12 e-font-12-res e-alt-font-poppins e-font-weight-400 line-height-20px">
                                                    Make sure the document is clear and all details are readable.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-3 px-sm-4 pb-4 pt-0">
                                    <PrimaryButton
                                        name="Continue"
                                        className="w-100 padding-12px-top padding-12px-bottom e-border-radius-10"
                                        loading={bankProofLoading}
                                        inActive={!bankProof}
                                        disabled={bankProofLoading || !bankProof}
                                        onPress={_handleContinue}
                                    />
                                </div>
                            </div>
                }
            </div>
        </div >
    );
}

export default VerifyBankModal;
